import { useUser } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import { deleteSwing, fetchSwingByUUID, Swing, updateSwingNameAndFavoriteStatus } from '@data';
import { activeUserIdAtom, useEnsureAtomValue } from '@store';

import { updateSwingNameInActivity } from '../actions/activitySwings';
import { invalidateFavoriteSwings } from '../actions/favoriteSwings';
import { updateSwingName } from '../actions/swing';
import { queryClient } from '../config/client';
import { queryKeys } from '../queryKeys';

import { getActivitySwingsKey } from './useActivitySwings';

export const getSwingKey = (uuid: string) => ['swings', uuid] as const;

export const useSwing = (uuid = '') => {
    const { data: user } = useUser();

    return useQuery({
        queryKey: getSwingKey(uuid),
        queryFn: () => fetchSwingByUUID(uuid),
        enabled: !!uuid && !!user,
        refetchOnMount: false,
    });
};

/**
 * Mutation to update swing to favorite by adding `name`,
 * or remove it from favorites by setting `name` to null.
 * @param activityId - needed for activity cache update action
 */
export const useUpdateSwingName = (activityId: number) => {
    return useMutation({
        mutationFn: ({ name, uuid }: Pick<Swing, 'name' | 'uuid'>) => updateSwingNameAndFavoriteStatus({ name, uuid }),
        onSuccess: (update) => {
            updateSwingName(update);
            updateSwingNameInActivity(activityId, update);
            invalidateFavoriteSwings();
        },
    });
};

type DeleteSwingParams = {
    activityId: number;
    swingUuid: string;
};

export const useDeleteSwing = () => {
    const activeUserId = useEnsureAtomValue(activeUserIdAtom);

    return useMutation({
        mutationFn: ({ swingUuid }: DeleteSwingParams) => deleteSwing(swingUuid),
        onMutate: async ({ swingUuid }) => {
            await queryClient.cancelQueries({
                queryKey: queryKeys.swings.analysis(activeUserId, swingUuid),
                exact: true,
            });
        },
        onSuccess: async (_, { activityId, swingUuid }) => {
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: getActivitySwingsKey(activityId),
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.activities.paged(activeUserId),
                    refetchType: 'all',
                }),
                queryClient.invalidateQueries({
                    queryKey: queryKeys.activities.count(activeUserId),
                }),
            ]);
            invalidateFavoriteSwings();
            queryClient.removeQueries({
                queryKey: queryKeys.swings.analysis(activeUserId, swingUuid),
                exact: true,
            });
            queryClient.removeQueries({ queryKey: getSwingKey(swingUuid), exact: true });
        },
    });
};
